import type { LevelAssessmentResult } from "@shared/labs/level-assessment";
import type {
  CalibrationCompetencyProgress,
  CalibrationCompetencyStatus,
  CalibrationRunsSummary,
  CalibrationSessionProfileView,
  CalibrationSessionView,
} from "@shared/labs/level-assessment-calibration-session";
import type { ReadinessVerdict } from "@shared/labs/level-assessment-readiness";
import { useCallback, useEffect, useRef, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { rpcClient } from "@/lib/rpc";
import { ReviewPanel } from "../results/review-panel";
import { C, DM, levelLabel, UI } from "../results/ui-tokens";
import { ReadinessBadge } from "./readiness-badge";

/**
 * Steps 2–4 of the create→calibrate workflow (provide reference profiles →
 * evaluate → validate per competency → auto-calibrate), driven entirely by a
 * calibration session id. Reused by:
 *   - the standalone re-calibration page (`/calibrate/:sessionId`), where Step 1
 *     (the matrix) already happened long ago; and
 *   - the `WorkflowShell`, embedded right after the matrix step (`matrixDone`),
 *     so model/discipline creation flows continuously into calibration.
 *
 * Client-facing (CHRO/recruiter) — plain-HR tone, no GEPA/prompt/matrix
 * internals. `onFinish` lets the host decide where "Concluir" navigates.
 */

interface SessionResponse {
  session: CalibrationSessionView;
  profiles: CalibrationSessionProfileView[];
  readiness: ReadinessVerdict;
  calibration: CalibrationRunsSummary;
  breakdown: CalibrationCompetencyProgress[];
  levelNames: Record<string, string>;
}

type SubStep = "collect" | "evaluating" | "validate" | "calibrating" | "done";

/** After this long without finishing, surface a "taking longer than expected"
 *  escape so the user is never stuck staring at a spinner (evaluation =
 *  multiple LLM calls + a scrape per profile; calibration = GEPA per
 *  competency). Both steps run in the background and poll for completion. */
const EVAL_SLOW_MS = 6 * 60 * 1000;
// A GEPA run may legitimately take up to its wall-clock cap (~60min) under LLM
// throttling, so "está demorando" only fires PAST the cap — when a run that
// should have self-terminated hasn't, i.e. it's genuinely stuck. Below this the
// calibrating panel shows live per-competency progress + a leave-and-return
// affordance instead of alarming the user.
const CALIB_SLOW_MS = 62 * 60 * 1000;

function stepFromStatus(status: CalibrationSessionView["status"]): SubStep {
  switch (status) {
    case "evaluating":
      return "evaluating";
    case "awaiting_validation":
      return "validate";
    case "calibrating":
      return "calibrating";
    case "completed":
    case "abandoned":
      return "done";
    default:
      return "collect";
  }
}

/** One LinkedIn URL per line → batch profiles (LinkedIn-primary for v1). */
function parseProfiles(text: string): { linkedinUrl: string }[] {
  return text
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l.length > 0)
    .slice(0, 20)
    .map((linkedinUrl) => ({ linkedinUrl }));
}

export function CalibrationSteps({
  sessionId,
  matrixDone = false,
  onFinish,
}: {
  sessionId: string;
  /** When true, the step indicator shows a completed leading "Matriz" step
   *  (the workflow case). Standalone re-calibration omits it. */
  matrixDone?: boolean;
  /** Called on "Concluir" after advancing the session to completed. */
  onFinish: () => void;
}) {
  const { toast } = useToast();

  const [data, setData] = useState<SessionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<SubStep>("collect");
  const [profilesText, setProfilesText] = useState("");
  const [busy, setBusy] = useState(false);
  const [validatedIds, setValidatedIds] = useState<Set<string>>(new Set());
  const [openProfileId, setOpenProfileId] = useState<string | null>(null);
  const [resultCache, setResultCache] = useState<Record<string, LevelAssessmentResult>>({});
  // Total profiles expected by the end of the current evaluation batch (prior
  // profiles + the ones just submitted) — drives the N/M progress bar.
  const [expectedCount, setExpectedCount] = useState(0);
  // Set when a background step (evaluating/calibrating) runs longer than its
  // slow threshold, so we can offer an escape instead of an endless spinner.
  const [slow, setSlow] = useState(false);
  const pollRef = useRef<{ cancelled: boolean } | null>(null);

  const loadSession = useCallback(
    async (opts?: { silent?: boolean }): Promise<SessionResponse | null> => {
      if (!opts?.silent) setLoading(true);
      try {
        const res = (await rpcClient.labs.levelAssessmentCalibrationSessionGet({
          sessionId,
        })) as SessionResponse;
        setData(res);
        setError(null);
        return res;
      } catch (e) {
        setError(e instanceof Error ? e.message : "Erro ao carregar a sessão de calibração.");
        return null;
      } finally {
        if (!opts?.silent) setLoading(false);
      }
    },
    [sessionId],
  );

  // Initial load — seed the step from the persisted session status.
  useEffect(() => {
    let active = true;
    (async () => {
      const res = await loadSession();
      if (active && res) setStep(stepFromStatus(res.session.status));
    })();
    return () => {
      active = false;
    };
  }, [loadSession]);

  // While evaluating (background batch), poll the session until it advances.
  // Each finished profile appears as a session-linked evaluation, so the profile
  // count climbs as a live N/M progress bar. The server moves an abandoned
  // session off `evaluating` (reaper / read-time staleness), so this terminates
  // even on a process recycle: awaiting_validation → validate, collecting (no
  // profiles finished) → back to collect. Elapsed is measured from the session's
  // own `updatedAt` so the slow escape is correct across reloads.
  useEffect(() => {
    if (step !== "evaluating") return;
    const token = { cancelled: false };
    pollRef.current = token;
    setSlow(false);
    const tick = async () => {
      const res = await loadSession({ silent: true });
      if (token.cancelled || !res) return;
      if (res.session.status === "awaiting_validation") {
        setStep("validate");
        return;
      }
      if (res.session.status === "collecting") {
        setStep("collect");
        return;
      }
      if (Date.now() - new Date(res.session.updatedAt).getTime() > EVAL_SLOW_MS) setSlow(true);
    };
    const id = setInterval(tick, 4000);
    void tick();
    return () => {
      token.cancelled = true;
      clearInterval(id);
    };
  }, [step, loadSession]);

  // While calibrating, poll until every run for the session has finished. A
  // stale/orphaned run is counted server-side as errored (not running), so this
  // always terminates; the slow flag (elapsed from the session's `updatedAt`,
  // i.e. when calibration started) offers an escape before that backstop.
  useEffect(() => {
    if (step !== "calibrating") return;
    const token = { cancelled: false };
    pollRef.current = token;
    setSlow(false);
    const tick = async () => {
      const res = await loadSession({ silent: true });
      if (token.cancelled || !res) return;
      if (res.calibration.total > 0 && res.calibration.running === 0) {
        setStep("done");
        return;
      }
      if (Date.now() - new Date(res.session.updatedAt).getTime() > CALIB_SLOW_MS) setSlow(true);
    };
    const id = setInterval(tick, 4000);
    void tick();
    return () => {
      token.cancelled = true;
      clearInterval(id);
    };
  }, [step, loadSession]);

  async function runBatch() {
    if (!data) return;
    const profiles = parseProfiles(profilesText);
    if (profiles.length === 0) {
      toast({
        title: "Adicione perfis",
        description: "Cole ao menos uma URL do LinkedIn (uma por linha).",
        variant: "destructive",
      });
      return;
    }
    setBusy(true);
    // Evaluation runs in the background; the request returns immediately and the
    // "evaluating" poll drives the transition to validation as profiles finish.
    setExpectedCount(data.profiles.length + profiles.length);
    try {
      await rpcClient.labs.levelAssessmentBatchRun({
        modelId: data.session.modelId,
        disciplineId: data.session.disciplineId,
        sessionId: data.session.id,
        profiles,
      });
      setProfilesText("");
      setStep("evaluating");
      await loadSession({ silent: true });
    } catch (e) {
      toast({
        title: "Falha ao iniciar a avaliação",
        description: e instanceof Error ? e.message : "Erro inesperado",
        variant: "destructive",
      });
      setStep("collect");
    } finally {
      setBusy(false);
    }
  }

  async function openReview(evaluationId: string) {
    if (!resultCache[evaluationId]) {
      try {
        const res = (await rpcClient.labs.levelAssessmentEvaluationGet({ evaluationId })) as {
          result: LevelAssessmentResult;
        };
        setResultCache((c) => ({ ...c, [evaluationId]: res.result }));
      } catch (e) {
        toast({
          title: "Não foi possível abrir o perfil",
          description: e instanceof Error ? e.message : "Erro inesperado",
          variant: "destructive",
        });
        return;
      }
    }
    setOpenProfileId(evaluationId);
  }

  function onProfileValidated(evaluationId: string) {
    setValidatedIds((s) => new Set(s).add(evaluationId));
    setOpenProfileId(null);
    void loadSession({ silent: true });
  }

  async function applyLearning() {
    if (!data || busy) return;
    setBusy(true);
    try {
      const res = (await rpcClient.labs.levelAssessmentCalibrationSessionCalibrate({
        sessionId: data.session.id,
      })) as { triggered: number; skipped: number };
      if (res.triggered === 0) {
        toast({
          title: "Nada para aprender ainda",
          description: "Valide ao menos uma competência antes de aplicar o aprendizado.",
          variant: "destructive",
        });
        return;
      }
      toast({
        title: "Modelo aprendendo",
        description: `${res.triggered} ${res.triggered === 1 ? "competência" : "competências"} em atualização.`,
      });
      setStep("calibrating");
    } catch (e) {
      toast({
        title: "Falha ao aplicar aprendizado",
        description: e instanceof Error ? e.message : "Erro inesperado",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  }

  async function calibrateMore() {
    if (!data || busy) return;
    setBusy(true);
    try {
      await rpcClient.labs.levelAssessmentCalibrationSessionAdvance({
        sessionId: data.session.id,
        status: "collecting",
      });
      setValidatedIds(new Set());
      setProfilesText("");
      setStep("collect");
      await loadSession({ silent: true });
    } catch (e) {
      toast({
        title: "Falha ao reiniciar a rodada",
        description: e instanceof Error ? e.message : "Erro inesperado",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  }

  async function finish() {
    if (!data || busy) return;
    setBusy(true);
    try {
      await rpcClient.labs.levelAssessmentCalibrationSessionAdvance({
        sessionId: data.session.id,
        status: "completed",
      });
      onFinish();
    } catch (e) {
      toast({
        title: "Falha ao concluir",
        description: e instanceof Error ? e.message : "Erro inesperado",
        variant: "destructive",
      });
      setBusy(false);
    }
  }

  if (loading && !data) return <Muted>Carregando sessão de calibração…</Muted>;
  if (error && !data) return <Muted>{error}</Muted>;
  if (!data) return null;

  const profiles = data.profiles;

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <ReadinessBadge readiness={data.readiness} />
      </div>
      <WorkflowStepper items={stepperItems(step, matrixDone)} />

      {step === "collect" && (
        <Panel title={`${matrixDone ? 2 : 1} · Perfis de referência`}>
          <p style={{ ...UI, fontSize: 13, color: C.ink70, marginTop: 0 }}>
            Cole uma URL do LinkedIn por linha (até 20). Você pode pular esta etapa e calibrar
            depois — o modelo já funciona, mas as estimativas ficam preliminares até a calibração.
          </p>
          <textarea
            data-testid="calibrate-profiles-input"
            value={profilesText}
            onChange={(e) => setProfilesText(e.target.value)}
            disabled={busy}
            rows={6}
            placeholder={
              "https://www.linkedin.com/in/exemplo-1\nhttps://www.linkedin.com/in/exemplo-2"
            }
            style={{
              ...UI,
              width: "100%",
              fontSize: 13,
              padding: 12,
              borderRadius: 10,
              border: `1px solid ${C.border}`,
              background: C.white,
              color: C.ink,
              resize: "vertical",
            }}
          />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 10,
              marginTop: 12,
              flexWrap: "wrap",
            }}
          >
            <PrimaryButton variant="outline" onClick={onFinish} testId="calibrate-skip">
              Calibrar depois
            </PrimaryButton>
            <PrimaryButton onClick={runBatch} disabled={busy} testId="calibrate-evaluate">
              Avaliar perfis
            </PrimaryButton>
          </div>
        </Panel>
      )}

      {step === "evaluating" && (
        <Panel title={`${matrixDone ? 2 : 1} · Avaliando perfis`}>
          <Muted>
            Avaliando os perfis de referência…{" "}
            {Math.min(profiles.length, expectedCount || profiles.length)}
            {expectedCount ? `/${expectedCount}` : ""} prontos. Pode levar alguns minutos — cada
            perfil é analisado competência por competência.
          </Muted>
          {slow && (
            <div style={{ marginTop: 12 }}>
              <p style={{ ...UI, fontSize: 13, color: C.ink70 }}>
                Está demorando mais que o esperado. Você pode validar os perfis já avaliados agora
                {profiles.length === 0
                  ? ""
                  : ` (${profiles.length} pronto${profiles.length === 1 ? "" : "s"})`}{" "}
                ou voltar mais tarde.
              </p>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {profiles.length > 0 ? (
                  <PrimaryButton
                    onClick={() => setStep("validate")}
                    testId="calibrate-eval-proceed"
                  >
                    Validar os já avaliados
                  </PrimaryButton>
                ) : (
                  <PrimaryButton onClick={() => setStep("collect")} testId="calibrate-eval-retry">
                    Adicionar perfis de novo
                  </PrimaryButton>
                )}
                <PrimaryButton variant="outline" onClick={onFinish} testId="calibrate-eval-later">
                  Voltar mais tarde
                </PrimaryButton>
              </div>
            </div>
          )}
        </Panel>
      )}

      {step === "validate" && (
        <Panel title={`${matrixDone ? 3 : 2} · Validação por competência`}>
          {profiles.length === 0 ? (
            <Muted>Nenhum perfil avaliado ainda. Volte e adicione perfis de referência.</Muted>
          ) : (
            <>
              <p style={{ ...UI, fontSize: 13, color: C.ink70, marginTop: 0 }}>
                Para cada perfil, confirme ou corrija a estimativa de cada competência.{" "}
                {validatedIds.size} de {profiles.length} validados.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {profiles.map((p) => {
                  const isOpen = openProfileId === p.evaluationId;
                  const isValidated = validatedIds.has(p.evaluationId);
                  const result = resultCache[p.evaluationId];
                  return (
                    <div key={p.evaluationId} data-testid={`calibrate-profile-${p.evaluationId}`}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          padding: "12px 14px",
                          borderRadius: 12,
                          border: `1px solid ${isValidated ? C.green : C.border}`,
                          background: isValidated ? C.greenBg : C.white,
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ ...DM, fontSize: 14, fontWeight: 700, color: C.ink }}>
                            {p.candidateName}
                          </div>
                          <div style={{ ...UI, fontSize: 12.5, color: C.ink50 }}>
                            Nível estimado: {levelLabel(p.overallLevel, data.levelNames)}
                          </div>
                        </div>
                        {isValidated ? (
                          <span
                            style={{ ...DM, fontSize: 12.5, fontWeight: 700, color: C.greenFg }}
                          >
                            Validado
                          </span>
                        ) : (
                          <PrimaryButton
                            variant="outline"
                            onClick={() =>
                              isOpen ? setOpenProfileId(null) : openReview(p.evaluationId)
                            }
                            testId={`calibrate-validate-${p.evaluationId}`}
                          >
                            {isOpen ? "Fechar" : "Validar"}
                          </PrimaryButton>
                        )}
                      </div>
                      {isOpen && result && (
                        <div style={{ marginTop: 8 }}>
                          <ReviewPanel
                            result={result}
                            reviewContext={{
                              modelId: data.session.modelId,
                              disciplineId: data.session.disciplineId,
                              evaluationId: p.evaluationId,
                              candidateName: p.candidateName,
                            }}
                            onClose={() => setOpenProfileId(null)}
                            onSaved={() => onProfileValidated(p.evaluationId)}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 10,
                  marginTop: 14,
                  flexWrap: "wrap",
                }}
              >
                <PrimaryButton variant="outline" onClick={() => setStep("collect")}>
                  Adicionar mais perfis
                </PrimaryButton>
                <PrimaryButton
                  onClick={applyLearning}
                  disabled={busy || validatedIds.size === 0}
                  testId="calibrate-apply"
                >
                  Aplicar aprendizado
                </PrimaryButton>
              </div>
            </>
          )}
        </Panel>
      )}

      {step === "calibrating" && (
        <Panel title={`${matrixDone ? 4 : 3} · O modelo está aprendendo`}>
          <Muted>
            Atualizando o modelo com base nas suas validações… {data.calibration.done}/
            {data.calibration.total} competências processadas
            {data.calibration.errored > 0 ? ` · ${data.calibration.errored} com erro` : ""}.
          </Muted>
          <CompetencyProgressList items={data.breakdown} />
          {/* A run can legitimately take several minutes per competency. Make
              the wait non-blocking: progress above updates live, and the user
              can leave at any time — the learning continues in the background. */}
          <p style={{ ...UI, fontSize: 13, color: C.ink70, marginTop: 10 }}>
            Cada competência pode levar alguns minutos. Você pode fechar esta tela e voltar quando
            quiser — o progresso é salvo e o aprendizado continua em segundo plano.
          </p>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 8 }}>
            <PrimaryButton variant="outline" onClick={onFinish} testId="calibrate-calib-leave">
              Sair — continua em segundo plano
            </PrimaryButton>
          </div>
          {slow && (
            <div style={{ marginTop: 12 }}>
              <p style={{ ...UI, fontSize: 13, color: C.ink70 }}>
                O aprendizado está demorando mais que o normal. As melhorias que já concluíram foram
                aplicadas; o resto continua em segundo plano. Você pode concluir agora e voltar
                depois — nada se perde.
              </p>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <PrimaryButton onClick={finish} disabled={busy} testId="calibrate-calib-finish">
                  Concluir mesmo assim
                </PrimaryButton>
                <PrimaryButton
                  variant="outline"
                  onClick={() => setStep("done")}
                  testId="calibrate-calib-summary"
                >
                  Ver resumo
                </PrimaryButton>
              </div>
            </div>
          )}
        </Panel>
      )}

      {step === "done" && (
        <Panel title="Aprendizado aplicado">
          <p style={{ ...UI, fontSize: 13.5, color: C.ink70, marginTop: 0 }}>
            {data.calibration.applied > 0
              ? `${data.calibration.applied} ${data.calibration.applied === 1 ? "competência foi aprimorada" : "competências foram aprimoradas"} com as suas validações.`
              : "As validações foram registradas. O modelo já estava bem ajustado para estas competências."}
          </p>
          {data.calibration.errored > 0 && (
            <p style={{ ...UI, fontSize: 12.5, color: C.ink50, marginTop: 0 }}>
              {data.calibration.errored}{" "}
              {data.calibration.errored === 1
                ? "competência não pôde ser processada e ficou como estava"
                : "competências não puderam ser processadas e ficaram como estavam"}
              . Você pode tentar calibrar de novo.
            </p>
          )}
          <CompetencyProgressList items={data.breakdown} />
          <div style={{ margin: "10px 0" }}>
            <ReadinessBadge readiness={data.readiness} showDetail />
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 8 }}>
            <PrimaryButton
              variant="outline"
              onClick={calibrateMore}
              disabled={busy}
              testId="calibrate-more"
            >
              Calibrar mais perfis
            </PrimaryButton>
            <PrimaryButton onClick={finish} disabled={busy} testId="calibrate-finish">
              Concluir
            </PrimaryButton>
          </div>
        </Panel>
      )}
    </>
  );
}

// ── Shared workflow UI (also used by WorkflowShell's Step 1) ────────────────

export interface StepItem {
  label: string;
  state: "done" | "active" | "upcoming";
}

function stepperItems(step: SubStep, matrixDone: boolean): StepItem[] {
  const order: SubStep[] = ["collect", "validate", "calibrating"];
  const labels: Record<string, string> = {
    collect: "Perfis",
    validate: "Validação",
    calibrating: "Aprendizado",
  };
  const activeIdx = order.findIndex(
    (k) =>
      k === step ||
      (step === "evaluating" && k === "collect") ||
      (step === "done" && k === "calibrating"),
  );
  const items: StepItem[] = order.map((k, i) => ({
    label: labels[k],
    state:
      step === "done" ? "done" : i < activeIdx ? "done" : i === activeIdx ? "active" : "upcoming",
  }));
  return matrixDone ? [{ label: "Matriz", state: "done" }, ...items] : items;
}

export function WorkflowStepper({ items }: { items: StepItem[] }) {
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }} data-testid="workflow-stepper">
      {items.map((s, i) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: fixed, ordered step list.
          key={i}
          style={{
            ...UI,
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 12.5,
            fontWeight: s.state === "active" ? 700 : 500,
            color: s.state === "upcoming" ? C.ink50 : C.ink,
          }}
        >
          <span
            style={{
              ...DM,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 22,
              height: 22,
              borderRadius: 999,
              fontSize: 11.5,
              fontWeight: 700,
              color: s.state === "upcoming" ? C.ink50 : C.white,
              background: s.state === "active" ? C.red : s.state === "done" ? C.green : C.ink10,
            }}
          >
            {i + 1}
          </span>
          {s.label}
        </div>
      ))}
    </div>
  );
}

export function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        maxWidth: 760,
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
        gap: 18,
        padding: "8px 0 40px",
      }}
    >
      {children}
    </div>
  );
}

export function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section
      style={{
        border: `1px solid ${C.border}`,
        borderRadius: 16,
        padding: 20,
        background: C.white,
      }}
    >
      <h2 style={{ ...DM, margin: "0 0 12px", fontSize: 15, fontWeight: 700, color: C.ink }}>
        {title}
      </h2>
      {children}
    </section>
  );
}

export function Muted({ children }: { children: React.ReactNode }) {
  return <p style={{ ...UI, fontSize: 13.5, color: C.ink50, margin: 0 }}>{children}</p>;
}

// ── Per-competency progress (calibrating + done panels) ─────────────────────

const COMPETENCY_STATE_STYLE: Record<
  CalibrationCompetencyStatus,
  { label: string; fg: string; bg: string }
> = {
  optimizing: { label: "Otimizando", fg: C.amberFg, bg: C.amberBg },
  improved: { label: "Aprimorada", fg: C.greenFg, bg: C.greenBg },
  no_change: { label: "Sem mudança", fg: C.ink50, bg: C.ink5 },
  failed: { label: "Não processada", fg: C.redHover, bg: C.red10 },
};

function elapsedLabel(ms: number): string {
  const min = Math.round(ms / 60000);
  return min < 1 ? "há menos de 1 min" : `há ~${min} min`;
}

/** One row per competency with a live status pill. Driven by the session's
 *  `breakdown` (the existing 4s poll re-renders it for free). Plain-HR labels —
 *  no axis/GEPA/prompt vocabulary. */
function CompetencyProgressList({ items }: { items: CalibrationCompetencyProgress[] }) {
  if (items.length === 0) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
      {items.map((c) => {
        const s = COMPETENCY_STATE_STYLE[c.status];
        return (
          <div
            key={`${c.scope}:${c.disciplineId ?? ""}:${c.axisId}`}
            data-testid={`calibrate-competency-${c.axisId}`}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "10px 14px",
              borderRadius: 12,
              border: `1px solid ${C.border}`,
              background: C.white,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ ...DM, fontSize: 13.5, fontWeight: 700, color: C.ink }}>{c.label}</div>
              {c.status === "optimizing" && c.elapsedMs != null && (
                <div style={{ ...UI, fontSize: 12, color: C.ink50 }}>
                  em andamento {elapsedLabel(c.elapsedMs)}
                </div>
              )}
            </div>
            <span
              style={{
                ...DM,
                flexShrink: 0,
                fontWeight: 700,
                fontSize: 12,
                padding: "4px 12px",
                borderRadius: 999,
                color: s.fg,
                background: s.bg,
              }}
            >
              {s.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export function PrimaryButton({
  children,
  onClick,
  disabled = false,
  variant = "solid",
  testId,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  variant?: "solid" | "outline";
  testId?: string;
}) {
  const solid = variant === "solid";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      style={{
        ...DM,
        fontWeight: 700,
        fontSize: 13,
        padding: "9px 18px",
        borderRadius: 999,
        cursor: disabled ? "not-allowed" : "pointer",
        background: disabled ? C.ink10 : solid ? C.ink : C.white,
        color: disabled ? C.ink50 : solid ? C.white : C.ink,
        border: `1px solid ${disabled ? C.ink10 : C.ink}`,
        opacity: disabled ? 0.8 : 1,
      }}
    >
      {children}
    </button>
  );
}
